import React, { forwardRef } from 'react'
import { css } from '@emotion/core'
import { BillboardButton, colors, useTheme } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import { boolAttr } from '@fs/zion-frontend-friends'
import { isExternalUrl } from '../helpers/urlHelpers'
import { navConfig } from '../../config'

const buttonHackCss = css`
  & {
    button,
    a {
      border-radius: 2em;
      text-transform: none;
      font-weight: 600;
      font-size: var(--text-size-b);
      white-space: normal !important;
      overflow: visible;
      word-break: unset;
      max-width: fit-content;
      text-align: center;
      height: auto;
      padding-bottom: 13px;
      overflow: hidden;
    }
  }

  &[bigness='sm'] {
    button,
    a {
      display: block !important;
    }
  }

  &:not([use-billboard-color]) {
    button,
    a {
      :not([aria-disabled='true']) {
        --button-text-color: ${colors.gray100};
        --button-hover-background: ${colors.transparent.gray05};
        --button-active-background: ${colors.transparent.gray10};
      }
    }

    &[emphasis='high'] {
      button,
      a {
        :not([aria-disabled='true']) {
          --button-hover-background: ${colors.gray80};
          --button-background: ${colors.gray100};
          --button-text-color: ${colors.gray00};
          --elev-shadow: 0;
          --elev-veil: 0;

          :hover:not(:aria-disabled=true):not(:active) {
            --elev-shadow: 0;
            --elev-veil: 0;
          }
        }
      }
    }
  }
`

const PillButton = forwardRef(({ emphasis = 'high', size = 'lg', children, to, color, ...props }, ref) => {
  const theme = useTheme()
  const external = to ? isExternalUrl(to) : false

  // HACK: we failed to add an exeption for links that go to *.familysearch.org that are not hosted by familysearch.
  const shouldPrependLangCode = navConfig.langCodeBlackList.some((langCode) => to?.includes(langCode))

  // This is a little trick to only send the prop if it is defined. Not sure why it still shows in the DOM when undefined, but I guess this'll work...
  const noPrependLocaleCode = shouldPrependLangCode ? { noPrependLocaleCode: true } : {}

  const themedColor = color || (theme === 'day' && emphasis === 'low' && 'blue')

  return (
    <div css={buttonHackCss} emphasis={emphasis} bigness={size} use-billboard-color={boolAttr(themedColor)}>
      <BillboardButton
        emphasis={emphasis}
        external={to ? external : undefined}
        target={external ? '_blank' : '_self'}
        to={to}
        color={themedColor}
        {...noPrependLocaleCode}
        {...props}
        ref={ref}
      >
        {children}
      </BillboardButton>
    </div>
  )
})
PillButton.propTypes = {
  emphasis: PropTypes.oneOf(['low', 'medium', 'high']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  children: PropTypes.node.isRequired,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  color: PropTypes.string,
  Icon: PropTypes.elementType,
}

export default PillButton
